import { NavigateFunction } from 'react-router-dom';

/**
 * Centralized navigation service for opening entities from notifications
 * Handles mapping entity types to routes and state keys
 */
export class NavigationService {
  /**
   * Map entity types to their corresponding routes
   */
  private static readonly ENTITY_ROUTES: Record<string, string> = {
    task: '/project-management/todos',
    checklist: '/project-management/checklists',
    specification: '/project-management/specifications',
    warranty: '/project-management/warranties',
    selection: '/project-management/selections',
    change_order: '/project-management/change-orders',
    material_order: '/project-management/material-orders',
    purchase_order: '/cost-management/purchase-orders',
    bill: '/cost-management/bills',
    invoice: '/cost-management/invoices',
    bid: '/cost-management/bids',
    bid_package: '/cost-management/bids',
    proposal: '/leads/proposals',
  };

  /**
   * Map entity types to their corresponding state keys
   */
  private static readonly ENTITY_STATE_KEYS: Record<string, string> = {
    task: 'openTaskId',
    checklist: 'openChecklistId',
    specification: 'openSpecificationId',
    warranty: 'openWarrantyId',
    selection: 'openSelectionId',
    change_order: 'openChangeOrderId',
    material_order: 'openMaterialOrderId',
    purchase_order: 'openPurchaseOrderId',
    bill: 'openBillId',
    invoice: 'openInvoiceId',
    bid: 'openBidId',
    bid_package: 'openBidPackageId',
    proposal: 'openProposalId',
  };

  /**
   * Get route for entity type, with fallback to notifications page
   */
  private static getRouteForEntityType(entityType: string): string {
    return this.ENTITY_ROUTES[entityType] || '/notifications';
  }

  /**
   * Get state key for entity type
   */
  private static getStateKeyForEntityType(entityType: string): string {
    return this.ENTITY_STATE_KEYS[entityType] || 'openEntityId';
  }

  /**
   * Open an entity by navigating to its route with the entity ID in state
   * @param entityType - The type of entity (task, checklist, warranty, etc.)
   * @param entityId - The ID of the entity to open
   * @param navigate - React Router's navigate function
   */
  static openEntity(
    entityType: string,
    entityId: string,
    navigate: NavigateFunction
  ): void {
    const route = this.getRouteForEntityType(entityType);
    const stateKey = this.getStateKeyForEntityType(entityType);

    navigate(route, {
      state: {
        [stateKey]: entityId,
      },
    });
  }

  /**
   * Where a link to ONE document goes (a `<Link to>`): the page of its type, told which record to open - the same address `openEntity` navigates to.
   * Bills are opened by their own `?bill=<id>` address (the Bills page reads it); every other type by the open-state the page reads with `useOpenEntity`.
   */
  static linkFor(entityType: string, entityId: string): { pathname: string; search?: string; state?: Record<string, string> } {
    const pathname = this.getRouteForEntityType(entityType);
    if (entityType === 'bill') return { pathname, search: `?bill=${encodeURIComponent(entityId)}` };
    return { pathname, state: { [this.getStateKeyForEntityType(entityType)]: entityId } };
  }

  /**
   * Clear the open state from the current location
   * This should be called after opening an entity to prevent reopening on refresh
   */
  static clearOpenState(): void {
    window.history.replaceState({}, document.title);
  }
}

